"use client";

import { usePathname, useRouter } from "next/navigation";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { AppImage as Image } from "@/components/ui/app-image";
import Link, { useLinkStatus } from "@/components/ui/link";
import { useAuthState } from "@/hooks/use-auth-state";
import { useTotalUnreadCount } from "@/hooks/use-chat";
import { useModalOverlayOpen } from "@/hooks/use-modal-overlay-open";
import { useShellProfileData } from "@/hooks/use-shell-profile-data";
import { firstMediaPreviewUrl } from "@/lib/media";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";
import { usePublishBottomNavShrunk } from "./bottom-navbar-state";
import {
  findNavIndex,
  isPlainPrimaryClick,
  navItems,
  recordPrimaryNavigation,
  scrollTabRootToTop,
} from "./primary-nav";

interface BottomNavbarProps {
  showLabels?: boolean;
}

function BottomNavbarPathnameSync({
  onPathnameChange,
}: {
  onPathnameChange: (pathname: string) => void;
}) {
  const pathname = usePathname();

  useEffect(() => {
    onPathnameChange(pathname);
  }, [onPathnameChange, pathname]);

  return null;
}

function PendingNavigationPulse() {
  const { pending } = useLinkStatus();

  return (
    <span
      aria-hidden="true"
      data-nav-pending={pending ? "true" : undefined}
      className={cn(
        "pointer-events-none absolute inset-1 rounded-full border border-brand/0 opacity-0",
        pending && "animate-pulse border-brand/35 opacity-100",
      )}
    />
  );
}

export function BottomNavbar({ showLabels = true }: BottomNavbarProps) {
  const router = useRouter();
  const { authStatus } = useAuthState();
  const { data: profile, isLoading } = useShellProfileData();
  const { data: unreadCount = 0 } = useTotalUnreadCount({
    enabled: authStatus === "authenticated",
  });
  const isOverlayOpen = useModalOverlayOpen();
  const profileImage = firstMediaPreviewUrl(profile?.images) ?? null;

  const [isCollapsedByScroll, setIsCollapsedByScroll] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragX, setDragX] = useState<number | null>(null);
  // Client-only data (profile avatar, unread count) is restored from the
  // persisted react-query cache *after* SSR, so it isn't available when the
  // server renders. Gate its reveal on mount so the first client render matches
  // the server HTML exactly — otherwise the avatar <img>/unread dot appear on
  // hydration where the server rendered a placeholder, and the resulting
  // hydration mismatch makes React re-render the whole tree (a full-page flash
  // on hard refresh across every page in this layout).
  const [mounted, setMounted] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [committedIndex, setCommittedIndex] = useState<number | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const activeIndexRef = useRef(activeIndex);
  const committedIndexRef = useRef(committedIndex);
  const pendingNavRef = useRef<string | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const activePointerIdRef = useRef<number | null>(null);
  const didDragRef = useRef(false);
  const suppressClickRef = useRef(false);
  const lastScrollYRef = useRef(0);
  const pathnameRef = useRef<string | null>(null);
  const isCollapsedByScrollRef = useRef(isCollapsedByScroll);
  isCollapsedByScrollRef.current = isCollapsedByScroll;

  useEffect(() => {
    lastScrollYRef.current = window.scrollY;

    const handleScroll = () => {
      const currentY = window.scrollY;
      const previousY = lastScrollYRef.current;
      const deltaY = currentY - previousY;

      if (currentY <= 24) {
        setIsCollapsedByScroll(false);
      } else if (deltaY > 2 && currentY > 80) {
        setIsCollapsedByScroll(true);
      } else if (deltaY < -2) {
        setIsCollapsedByScroll(false);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const syncPathname = useCallback((pathname: string) => {
    pathnameRef.current = pathname;
    const matchedIndex = findNavIndex(pathname);
    committedIndexRef.current = matchedIndex;
    setCommittedIndex(matchedIndex);

    if (matchedIndex !== null) {
      activeIndexRef.current = matchedIndex;
      setActiveIndex(matchedIndex);
      if (pendingNavRef.current === navItems[matchedIndex]?.href) {
        pendingNavRef.current = null;
      }
    }
    setIsCollapsedByScroll(false);
    lastScrollYRef.current = 0;
  }, []);

  const isShrunk = isCollapsedByScroll && !isHovered && !isDragging;
  usePublishBottomNavShrunk(isShrunk);

  useEffect(() => {
    setMounted(true);
  }, []);

  const updateDragPosition = (clientX: number, clientY: number) => {
    if (!navRef.current) return;
    const rect = navRef.current.getBoundingClientRect();

    // Ignore swipe selector if fingers drag too far vertically outside the navbar region (+40px for safety)
    if (clientY < rect.top - 40 || clientY > rect.bottom + 40) {
      return;
    }

    const relativeX = clientX - rect.left;
    const rectWidth = rect.width;
    if (rectWidth <= 0) return;

    const bubbleWidth = (rectWidth - 2 * paddingPx) / navItems.length;
    const maxTranslateX = (navItems.length - 1) * bubbleWidth;
    const desiredTranslateX = relativeX - paddingPx - bubbleWidth / 2;
    const clampedTranslateX = Math.max(
      0,
      Math.min(maxTranslateX, desiredTranslateX),
    );

    setDragX(clampedTranslateX);

    const index = Math.max(
      0,
      Math.min(
        navItems.length - 1,
        Math.round(clampedTranslateX / bubbleWidth),
      ),
    );

    // A tick each time the scrubbed bubble lands on a new tab.
    if (index !== activeIndexRef.current) uiHaptics.select();
    activeIndexRef.current = index;
    setActiveIndex(index);
  };

  const recordNavigation = (targetItem: (typeof navItems)[number]) =>
    recordPrimaryNavigation(targetItem, "bottom_bar");

  const triggerNavigation = (index: number) => {
    const targetItem = navItems[index];
    if (!targetItem) return;

    const isActive = index === committedIndexRef.current;

    if (!isActive && pendingNavRef.current !== targetItem.href) {
      pendingNavRef.current = targetItem.href;
      router.push(targetItem.href);
      recordNavigation(targetItem);
    }
  };

  const selectNavigationTarget = (
    event: React.MouseEvent<HTMLAnchorElement>,
    index: number,
  ) => {
    // Modified clicks keep the current page open, so they should not move the
    // active indicator in this tab.
    if (!isPlainPrimaryClick(event)) return;

    activeIndexRef.current = index;
    setActiveIndex(index);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;

    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    activePointerIdRef.current = e.pointerId;
    didDragRef.current = false;
    if (e.pointerType === "mouse") {
      setIsHovered(true);
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLElement>) => {
    if (
      activePointerIdRef.current !== e.pointerId ||
      !pointerStartRef.current
    ) {
      return;
    }

    const movedX = e.clientX - pointerStartRef.current.x;
    const movedY = e.clientY - pointerStartRef.current.y;
    if (!didDragRef.current) {
      // iOS reports a few pixels of pointer jitter for an ordinary tap. Only
      // enter the swipe selector after a deliberate, predominantly horizontal
      // gesture; otherwise leave the native Link click completely untouched.
      if (Math.abs(movedX) < 14) return;
      if (Math.abs(movedX) <= Math.abs(movedY) * 1.25) {
        pointerStartRef.current = null;
        activePointerIdRef.current = null;
        setIsHovered(false);
        return;
      }
    }

    if (!didDragRef.current) {
      didDragRef.current = true;
      setIsDragging(true);
      navRef.current?.setPointerCapture(e.pointerId);
    }

    if (e.cancelable) e.preventDefault();
    updateDragPosition(e.clientX, e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLElement>) => {
    if (activePointerIdRef.current !== e.pointerId) return;

    const wasDragging = didDragRef.current;
    const targetIndex = activeIndexRef.current;
    if (wasDragging && targetIndex !== null) {
      // A drag is the only path that uses router.push. A normal tap is left to
      // the Link below, so Safari/PWA pointer capture cannot cause two
      // competing navigations or suppress the Link click.
      e.preventDefault();
      suppressClickRef.current = true;
      triggerNavigation(targetIndex);
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }

    setIsDragging(false);
    setDragX(null);
    pointerStartRef.current = null;
    activePointerIdRef.current = null;
    didDragRef.current = false;

    try {
      navRef.current?.releasePointerCapture(e.pointerId);
    } catch (_err) {}

    // Delay collapsing to allow transitions to finish
    setTimeout(() => setIsHovered(false), 1000);
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLElement>) => {
    if (activePointerIdRef.current !== e.pointerId) return;

    if (committedIndexRef.current !== null) {
      activeIndexRef.current = committedIndexRef.current;
      setActiveIndex(committedIndexRef.current);
    }

    setIsDragging(false);
    setDragX(null);
    pointerStartRef.current = null;
    activePointerIdRef.current = null;
    didDragRef.current = false;

    try {
      navRef.current?.releasePointerCapture(e.pointerId);
    } catch (_err) {}

    setIsHovered(false);
  };

  const paddingPx = 8;

  return (
    <nav
      aria-label="Primary navigation"
      data-bottom-navbar
      ref={navRef}
      onPointerEnter={(e) => {
        if (e.pointerType === "mouse") {
          setIsHovered(true);
        }
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === "mouse") {
          setIsHovered(false);
        }
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      className={cn(
        // Centred between the side safe areas, not the screen: iPhone Duo
        // puts the status bar on a side edge (~84pt), which the bar must
        // clear like the page content does.
        "pointer-events-auto fixed right-[calc(env(safe-area-inset-right,0px)+1rem)] bottom-(--bottom-nav-offset) left-[calc(env(safe-area-inset-left,0px)+1rem)] z-100 mx-auto flex max-w-md origin-bottom touch-pan-y items-center justify-around rounded-full border border-black/10 bg-white/78 p-2 text-text-primary shadow-[0_15px_35px_rgba(15,23,42,0.18),inset_0_1px_1.5px_rgba(255,255,255,0.9)] backdrop-blur-2xl backdrop-saturate-150 transition-[transform,opacity,box-shadow,height] duration-(--bottom-nav-duration) ease-(--bottom-nav-ease) select-none dark:border-white/10 dark:bg-black/72 dark:text-white dark:shadow-[0_15px_35px_rgba(0,0,0,0.55),inset_0_1px_1.5px_rgba(255,255,255,0.15)]",
        // Geometry lives in globals.css (`--bottom-nav-*`) so floating
        // buttons above the bar derive from the same numbers.
        isShrunk
          ? "h-(--bottom-nav-collapsed-height) translate-y-0 scale-[0.98] opacity-98 shadow-md"
          : "h-(--bottom-nav-height) translate-y-0 scale-100 opacity-100",
        // Slide fully out of the way while any modal drawer is open —
        // bottom sheets must never fight the navbar for the bottom edge.
        isOverlayOpen && "pointer-events-none translate-y-[150%] opacity-0",
        // From `rail:` the side rail (components/shell/app-side-rail) is the
        // navigation. A floating pill centred on a wide window sits exactly on
        // iPhone Duo's fold, and on a landscape phone it covers a quarter of
        // the screen.
        "rail:hidden",
      )}
    >
      {/* Sliding liquid glass indicator bubble */}
      <div
        className={cn(
          "pointer-events-none absolute top-2 bottom-2 z-0 rounded-full border border-black/8 bg-white/88 shadow-[0_5px_18px_rgba(15,23,42,0.1),inset_0_1px_1px_rgba(255,255,255,0.95)] dark:border-white/15 dark:bg-white/10 dark:shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)]",
          isDragging
            ? "transition-none"
            : "transition-[transform,opacity] duration-400 ease-[cubic-bezier(0.16,1,0.3,1)]",
          activeIndex === null && dragX === null && "opacity-0",
        )}
        style={{
          width: `calc((100% - ${2 * paddingPx}px) / ${navItems.length})`,
          transform:
            dragX !== null
              ? `translateX(${dragX}px)`
              : `translateX(${(activeIndex ?? 0) * 100}%)`,
          left: `${paddingPx}px`,
        }}
      />

      {navItems.map((item, index) => {
        const isActive = index === activeIndex;
        const isCurrentPage = index === committedIndex;
        const Icon = item.icon;
        const isProfile = item.label === "Profile";
        const isChat = item.label === "Chats";
        const hasUnread = mounted && isChat && unreadCount > 0;

        return (
          <Link
            key={item.href}
            href={item.href}
            // Viewport prefetch OFF — see back-aware-link.tsx for the
            // measurement. These links live in the persistent shell, so they
            // are in the viewport on every page for the whole session, and a
            // prefetch that comes back EMPTY is never satisfied: the router
            // re-requests it on every re-evaluation. Measured on a
            // production build with only programmatic scrolling and no
            // pointer events: 2,390 requests to one of these five URLs.
            // They are the primary navigation and are tapped directly, so
            // there is nothing to gain from warming them in the background.
            prefetch={false}
            aria-current={isCurrentPage ? "page" : undefined}
            onClick={(event) => {
              if (suppressClickRef.current) {
                event.preventDefault();
                event.stopPropagation();
                return;
              }

              // Already on this tab's root page: no navigation (a same-URL
              // push would refetch the page for nothing) — scroll to top
              // instead. A nested page under the tab (/hiives/x) still
              // falls through and navigates to the root, the other half of
              // the iOS convention. The bar only ticks on tab CHANGES, so
              // this tap gets its own tick.
              if (
                isCurrentPage &&
                pathnameRef.current === item.href &&
                isPlainPrimaryClick(event)
              ) {
                event.preventDefault();
                uiHaptics.select();
                recordNavigation(item);
                scrollTabRootToTop(item.href);
                return;
              }

              // Next may still be waiting for a cold Server Component payload.
              // Move the indicator on intent so the tab bar responds on the
              // same frame; pathname remains the source of truth after the
              // transition settles.
              if (!isCurrentPage) uiHaptics.select();
              selectNavigationTarget(event, index);
              recordNavigation(item);
            }}
            className={cn(
              "relative z-10 flex min-w-0 flex-1 flex-col items-center justify-center rounded-full py-2.5 transition-[color,transform] duration-150 ease-out-strong",
              isShrunk
                ? ""
                : "active:scale-[0.92] pointer-fine:hover:scale-[1.05]",
              isActive
                ? "font-bold text-text-primary dark:text-white"
                : "text-text-secondary hover:text-text-primary dark:text-white/72 dark:hover:text-white",
            )}
          >
            <PendingNavigationPulse />
            <div className="relative h-5.5 w-5.5 shrink-0">
              {isProfile && (!mounted || isLoading) ? (
                <div className="h-full w-full overflow-hidden rounded-[30%] bg-surface-alt">
                  <div className="h-full w-full animate-pulse bg-gradient-to-br from-brand/20 via-accent/20 to-surface-alt" />
                </div>
              ) : isProfile && profileImage ? (
                <div className="h-full w-full overflow-hidden rounded-[30%]">
                  <Image
                    src={profileImage}
                    alt="Profile"
                    width={22}
                    height={22}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <Icon className="h-full w-full" />
              )}
              {/* Notification indicator dot */}
              {hasUnread && (
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full border border-white bg-accent dark:border-white/20" />
              )}
            </div>
            {showLabels && (
              <div
                className={cn(
                  "flex flex-col items-center overflow-hidden transition-[max-height,margin,transform,opacity] duration-400 ease-[cubic-bezier(0.16,1,0.3,1)]",
                  isShrunk
                    ? "pointer-events-none mt-0 max-h-0 scale-50 opacity-0"
                    : "mt-1 max-h-4 scale-100 opacity-100",
                )}
              >
                <span className="truncate text-[10px] font-semibold tracking-wide">
                  {item.label}
                </span>
              </div>
            )}
          </Link>
        );
      })}
      <Suspense fallback={null}>
        <BottomNavbarPathnameSync onPathnameChange={syncPathname} />
      </Suspense>
    </nav>
  );
}
